import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, FileDown, Loader2, Package, ShoppingBag, Truck } from 'lucide-react';
import {
  fetchBoutiqueCommandes,
  genererEtiquetteCommande,
  modifierCommandeBoutique,
  type CommandeBoutique,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { euros } from '../utils/format';

import { dateHeureFr } from '../utils/dates';
const STATUTS: Record<string, string> = {
  nouvelle: 'Nouvelle',
  preparee: 'Préparée',
  expediee: 'Expédiée',
  // Le retrait au local n'est plus proposé, mais les commandes d'avant le
  // portent encore : sans cette entrée, leur état s'afficherait vide.
  retiree: 'Retirée',
  annulee: 'Annulée (re-stockée)',
};

/**
 * Les commandes de la boutique : ce qu'il reste à préparer et à expédier.
 *
 * L'écran vit en Comptabilité parce que c'est là qu'on vient le chercher :
 * une commande est une recette encaissée et un colis à faire partir. Le
 * catalogue, lui, se règle une fois par saison et a rejoint l'Administration.
 *
 * Les nouvelles d'abord : c'est le seul état qui demande une action.
 */
export default function BureauBoutiquePage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const peutVoir = aLeDroit('voir_boutique');
  const peutGerer = aLeDroit('gerer_boutique');
  const queryClient = useQueryClient();

  const commandesQuery = useQuery({
    queryKey: ['bureau-boutique-commandes'],
    queryFn: fetchBoutiqueCommandes,
    enabled: peutVoir,
  });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['bureau-boutique-commandes'] });

  if (authLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">La boutique dépend de votre fonction au bureau : un administrateur peut l'ouvrir dans la grille de droits.</p>
          </div>
        </div>
      </div>
    );
  }

  const commandes = commandesQuery.data?.commandes ?? [];
  const aTraiter = commandes.filter((c) => c.statut === 'nouvelle' || c.statut === 'preparee');
  const closes = commandes.filter((c) => c.statut !== 'nouvelle' && c.statut !== 'preparee');

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <ShoppingBag className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Commandes de la boutique</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Ce qui reste à préparer et à expédier. Le catalogue se règle en Administration.
          </p>
        </div>
      </div>

      {commandesQuery.isPending ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      ) : commandes.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune commande pour le moment.</p>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              À traiter ({aTraiter.length})
            </h2>
            {aTraiter.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Rien en attente : tout est parti.</p>
            ) : (
              aTraiter.map((c) => (
                <LigneCommande key={c.id} commande={c} peutGerer={peutGerer} onChange={rafraichir} />
              ))
            )}
          </section>

          {closes.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Terminées ({closes.length})
              </h2>
              {closes.map((c) => (
                <LigneCommande key={c.id} commande={c} peutGerer={peutGerer} onChange={rafraichir} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function LigneCommande({ commande: c, peutGerer, onChange }: {
  commande: CommandeBoutique;
  peutGerer: boolean;
  onChange: () => void;
}) {
  const [suivi, setSuivi] = useState<string | null>(null);

  const modifier = useMutation({
    mutationFn: (payload: { statut?: string; numeroSuivi?: string | null }) =>
      modifierCommandeBoutique(c.id, payload),
    onSuccess: onChange,
  });

  const etiquette = useMutation({
    mutationFn: () => genererEtiquetteCommande(c.id),
    onSuccess: onChange,
  });

  return (
    <div className="card space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-gray-500">{c.reference}</span>
        <strong className="text-gray-900 dark:text-gray-100">{c.clientNom}</strong>
        <a href={`mailto:${c.clientEmail}`} className="text-sm text-primary-700 dark:text-primary-300">{c.clientEmail}</a>
        {c.clientTelephone && <span className="text-sm text-gray-500">{c.clientTelephone}</span>}
        <span className="ml-auto font-bold tabular-nums">{euros.format(c.total)}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {c.lignes.map((l) => `${l.quantite} × ${l.nom}${l.taille ? ` (${l.taille})` : ''}`).join(' · ')}
        {c.fraisExpedition > 0 && <> — <Truck className="w-3.5 h-3.5 inline" /> port {euros.format(c.fraisExpedition)}</>}
      </p>
      {c.adresseLivraison && <p className="text-xs text-gray-500 whitespace-pre-line">📍 {c.adresseLivraison}</p>}
      {c.commentaire && <p className="text-xs text-gray-500 italic">« {c.commentaire} »</p>}
      {/* L'étiquette de transport. Le bureau prépare le colis, clique,
          imprime et colle : le numéro de suivi arrive avec, et le client le
          reçoit dès qu'on passe la commande en « expédiée ». Recopier à la
          main une adresse et un numéro à treize caractères, c'est deux
          endroits où la faute de frappe coûte un colis. */}
      {peutGerer && (
        <div className="flex flex-wrap items-center gap-2">
          {c.etiquetteUrl ? (
            <a
              href={c.etiquetteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <FileDown className="w-4 h-4" /> Étiquette {c.transporteur ?? ''}
            </a>
          ) : etiquette.isPending ? (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <Loader2 className="w-4 h-4 animate-spin" /> Demande de l'étiquette…
            </span>
          ) : (c.etiquetteManques?.length ?? 0) === 0 ? (
            <button
              type="button"
              onClick={() => etiquette.mutate()}
              className="btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <FileDown className="w-4 h-4" /> Générer l'étiquette
            </button>
          ) : null}

          {etiquette.isError && (
            <span className="text-sm text-red-600">{(etiquette.error as Error).message}</span>
          )}
        </div>
      )}

      {peutGerer && !c.etiquetteUrl && (c.etiquetteManques?.length ?? 0) > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Étiquette automatique indisponible : il manque {c.etiquetteManques!.join(', ')}.
        </p>
      )}

      {peutGerer && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
          <select className="input w-auto text-sm" value={c.statut}
            onChange={(e) => modifier.mutate({ statut: e.target.value })}>
            {Object.entries(STATUTS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="input flex-1 min-w-[10rem] text-sm" placeholder="N° de suivi du colis"
            value={suivi ?? c.numeroSuivi ?? ''}
            onChange={(e) => setSuivi(e.target.value)} />
          <button className="btn-outline text-sm px-3 py-1.5"
            onClick={() => modifier.mutate({ numeroSuivi: suivi ?? c.numeroSuivi ?? '', statut: 'expediee' })}>
            <Package className="w-4 h-4 inline mr-1" /> Expédiée
          </button>
          <span className="ml-auto text-xs text-gray-400">{dateHeureFr(c.createdAt)}</span>
        </div>
      )}
    </div>
  );
}
